import { groupHasBuildings, ownsCompleteGroup } from '../engine/rules.js';

const RESERVE = 220;

function transferable(game, player, id) {
  const property = game.getProperty(id), cell = game.board[id];
  return property?.ownerId === player?.id && property.houses === 0
    && (cell.type !== 'street' || !groupHasBuildings(game, cell.group));
}

function portfolioValue(game, ids) {
  let value = 0;
  const groups = new Set();
  for (const id of ids) {
    const property = game.getProperty(id), cell = game.board[id];
    value += Math.max(0, cell.price - (property.mortgaged ? Math.ceil(cell.mortgageValue * 1.1) : 0));
    if (cell.type === 'street') groups.add(cell.group);
  }
  for (const group of groups) {
    const cells = game.board.filter(cell => cell.type === 'street' && cell.group === group);
    if (cells.every(cell => ids.has(cell.index) && !game.getProperty(cell.index).mortgaged)) {
      value += cells.reduce((sum, cell) => sum + cell.price, 0) * 0.6;
    }
  }
  for (const type of ['transport', 'utility']) {
    const cells = game.board.filter(cell => cell.type === type && ids.has(cell.index) && !game.getProperty(cell.index).mortgaged);
    value += cells.reduce((sum, cell) => sum + cell.price, 0) * 0.2 * Math.max(0, cells.length - 1);
  }
  return value;
}

// This is only a decision policy; GameEngine revalidates and applies the trade atomically.
export function botWantsTrade(game, player, trade) {
  const from = game.getPlayer(trade.fromPlayerId), to = game.getPlayer(trade.toPlayerId);
  if (!from || !to || from.bankrupt || to.bankrupt || game.pendingDebt) return false;
  if (from.money < trade.offeredMoney || to.money < trade.requestedMoney) return false;
  if (!trade.offeredPropertyIds.every(id => transferable(game, from, id))
    || !trade.requestedPropertyIds.every(id => transferable(game, to, id))) return false;
  const sender = player.id === from.id;
  const given = sender ? trade.offeredPropertyIds : trade.requestedPropertyIds;
  const received = sender ? trade.requestedPropertyIds : trade.offeredPropertyIds;
  const spent = sender ? trade.offeredMoney : trade.requestedMoney;
  const earned = sender ? trade.requestedMoney : trade.offeredMoney;
  if (player.money + earned - spent < Math.min(RESERVE, player.money)) return false;
  const before = new Set(game.properties.filter(property => property.ownerId === player.id).map(property => property.id));
  const after = new Set([...before].filter(id => !given.includes(id)));
  received.forEach(id => after.add(id));
  return earned - spent + portfolioValue(game, after) - portfolioValue(game, before) >= Math.max(10, spent * 0.05);
}

function completesCollection(game, player, id) {
  const cell = game.board[id];
  if (cell.type === 'street') {
    return game.board.filter(other => other.type === 'street' && other.group === cell.group && other.index !== id)
      .every(other => game.getProperty(other.index).ownerId === player.id);
  }
  return game.properties.some(property => property.id !== id && property.ownerId === player.id && game.board[property.id].type === cell.type);
}

export function proposeBotTrade(game, player) {
  if (game.pendingDebt || game.trades.some(trade => trade.status === 'pending' && [trade.fromPlayerId, trade.toPlayerId].includes(player.id))) return null;
  const reserved = new Set(game.trades.filter(trade => trade.status === 'pending')
    .flatMap(trade => [...trade.offeredPropertyIds, ...trade.requestedPropertyIds]));
  const candidates = game.properties.filter(property => property.ownerId && property.ownerId !== player.id && !property.mortgaged
    && !reserved.has(property.id) && completesCollection(game, player, property.id));
  for (const property of candidates) {
    const seller = game.getPlayer(property.ownerId);
    if (!seller || seller.bankrupt || !transferable(game, seller, property.id)) continue;
    const base = { fromPlayerId: player.id, toPlayerId: seller.id, requestedPropertyIds: [property.id] };
    const price = game.board[property.id].price;
    // Prefer an exchange that completes a collection for both participants.
    for (const offered of game.properties.filter(item => item.ownerId === player.id && !item.mortgaged && !reserved.has(item.id))) {
      if (!transferable(game, player, offered.id) || ownsCompleteGroup(game, player.id, game.board[offered.id].group)
        || !completesCollection(game, seller, offered.id)) continue;
      const difference = price - game.board[offered.id].price;
      const proposal = { ...base, offeredPropertyIds: [offered.id], offeredMoney: Math.max(0, difference), requestedMoney: Math.max(0, -difference) };
      if (botWantsTrade(game, player, proposal) && botWantsTrade(game, seller, proposal)) return proposal;
    }
    const proposal = { ...base, offeredPropertyIds: [], offeredMoney: Math.ceil(price * 1.2), requestedMoney: 0 };
    if (botWantsTrade(game, player, proposal) && (!seller.isBot || botWantsTrade(game, seller, proposal))) return proposal;
  }
  return null;
}
